import { Link } from 'react-router';

import { cn } from '@/lib/cn';

export type BrandSize = 'sm' | 'md' | 'lg';

/**
 * The lockup artwork is 2075x758, so about 2.74:1. Heights are set explicitly
 * and the width follows.
 */
const SIZES: Record<BrandSize, { lockup: string; mark: string }> = {
  sm: { lockup: 'h-7', mark: 'h-6 w-6' },
  md: { lockup: 'h-10', mark: 'h-9 w-9' },
  lg: { lockup: 'h-16', mark: 'h-14 w-14' },
};

export interface BrandProps {
  size?: BrandSize;
  /** Square mark only, without the wordmark. For a collapsed rail. */
  markOnly?: boolean;
  /** Renders as a plain block instead of a link home. */
  asLink?: boolean;
  className?: string;
}

/**
 * The WinDocs lockup - mark and typography together, exactly as supplied.
 *
 * Two artworks swapped by CSS: the light one has a navy "Win" that would
 * vanish on a dark bar, the dark one a white "Win". Both are genuinely
 * transparent (verified by compositing each onto a solid colour - an earlier
 * revision of the light file had a transparency checkerboard baked into its
 * pixels and rendered as a grey box).
 *
 * They are WebP at 360px, around 11 KB each, so shipping both costs less than
 * one of the 500 KB source PNGs and the theme switch needs no second request.
 */
export function Brand({ size = 'md', markOnly = false, asLink = true, className }: BrandProps) {
  const { lockup, mark } = SIZES[size];

  const content = markOnly ? (
    <img src="/favicon-96x96.png" alt="WinDocs" className={cn('shrink-0', mark)} />
  ) : (
    <>
      <img
        src="/windocs-logo.webp"
        alt="WinDocs"
        className={cn('w-auto shrink-0 dark:hidden', lockup)}
      />
      <img
        src="/windocs-logo-dark.webp"
        alt="WinDocs"
        aria-hidden="true"
        className={cn('hidden w-auto shrink-0 dark:block', lockup)}
      />
    </>
  );

  const classes = cn('flex shrink-0 items-center', className);

  if (!asLink) {
    return <div className={classes}>{content}</div>;
  }

  return (
    <Link to="/" aria-label="WinDocs home" className={classes}>
      {content}
    </Link>
  );
}
